import styles from "./Contact.module.scss";
import { Form, FormInstance } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import useAppSelector from "../../../hooks/useAppSelector";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import FormItemSelect from "../../../components/ui/FormItemSelect/FormItemSelect";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import { openSuccessMessage } from "../../../store/message/messageSlice";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { IContactInformation } from "../../../models/ContactInformation";
import { useGetContactInformationQuery } from "../../../api/queries";
import { useContactInformationMutations } from "../../../api/mutations";
import { handleError } from "../../../helper/errorHandler";

const Contact = () => {
  const formRef = useRef<FormInstance>(null);
  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { allCountries } = useAppSelector((state) => state.countriesSlice);
  const [countries, setCountries] = useState<{ id: string; label: string }[]>([]);
  const [phoneCountryCodes, setPhoneCountryCodes] = useState<{ id: string; label: string }[]>([]);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: contactInformation, refetch } = useGetContactInformationQuery(user?.id || null);
  const { updateById } = useContactInformationMutations();

  useEffect(() => {
    if (allCountries.length > 0) {
      const changedCountries = allCountries
        .map((el) => {
          return {
            id: el.cca2,
            label: el.name.common,
          };
        })
        .sort((a, b) =>
          a.label.toLowerCase() < b.label.toLowerCase()
            ? -1
            : a.label.toLowerCase() > b.label.toLowerCase()
              ? 1
              : 0
        );
      setCountries(changedCountries);
      const changedCountriesForCountryCodes = allCountries
        .map((el) => {
          return {
            id:
              (el.idd.root ? el.idd.root : "+999") +
              (el.idd.suffixes.length ? el.idd.suffixes[0] : ""),
            label:
              (el.idd.root ? el.idd.root : "+999") +
              (el.idd.suffixes.length ? el.idd.suffixes[0] : "") +
              " " +
              el.name.common,
          };
        })
        .sort((a, b) =>
          a.label.toLowerCase() < b.label.toLowerCase()
            ? -1
            : a.label.toLowerCase() > b.label.toLowerCase()
              ? 1
              : 0
        );
      setPhoneCountryCodes(changedCountriesForCountryCodes);
    }
  }, [allCountries]);

  useEffect(() => {
    if (!contactInformation) return;

    if (formRef.current) {
      formRef.current.setFieldsValue(contactInformation);
    }
  }, [contactInformation]);

  const onFinish = async (values: Omit<IContactInformation, "id" | "userId">) => {
    if (!user || !contactInformation) return;

    const model: IContactInformation = {
      id: contactInformation?.id,
      userId: user?.id,
      addressLine1: values.addressLine1 || null,
      addressLine2: values.addressLine2 || null,
      city: values.city || null,
      postalCode: values.postalCode || null,
      countryCode: values.countryCode || null,
      phoneCountryCode: values.phoneCountryCode || null,
      phoneNumber: values.phoneNumber || null,
      emergencyName: values.emergencyName || null,
      emergencyPhoneCountryCode: values.emergencyPhoneCountryCode || null,
      emergencyPhoneNumber: values.emergencyPhoneNumber || null,
    };

    updateById(
      {
        contactInformation: model,
      },
      {
        onSuccess: () => {
          dispatch(openSuccessMessage(t("ContactInformationUpdatedSuccessfully")));

          refetch();
        },
        onError: (e) => handleError(e, dispatch, t),
      }
    );
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t("ContactInformation")}</h2>
      <Form
        className={styles.form}
        ref={formRef}
        onFinish={onFinish}
        name="contact-information"
        layout={"vertical"}
      >
        <FormItemInput
          name={"addressLine1"}
          label={t("AddressLine") + " 1"}
          rules={{ required: false }}
          isSettingInput={true}
        />
        <FormItemInput
          name={"addressLine2"}
          label={t("AddressLine") + " 2"}
          rules={{ required: false }}
          isSettingInput={true}
        />

        <div className={styles.row}>
          <FormItemInput
            name={"city"}
            label={t("City")}
            rules={{ required: false }}
            isSettingInput={true}
          />
          <FormItemInput
            name={"postalCode"}
            label={t("PostalCode")}
            rules={{ required: false }}
            isSettingInput={true}
          />
        </div>
        <FormItemSelect
          name={"countryCode"}
          label={t("Country")}
          rules={{ required: false }}
          items={countries}
          isSettingInput={true}
          showSearch={true}
        />

        <div className={`${styles.row} ${styles.adaptive}`}>
          <div className={styles.w40}>
            <FormItemSelect
              name={"phoneCountryCode"}
              label={t("PhoneCountryCode")}
              rules={{ required: false }}
              items={phoneCountryCodes}
              isSettingInput={true}
              showSearch={true}
            />
          </div>

          <div className={styles.w60}>
            <FormItemInput
              name={"phoneNumber"}
              label={t("PhoneNumber")}
              rules={{ required: false }}
              isSettingInput={true}
            />
          </div>
        </div>

        <div className={styles.border}></div>
        <h3 className={styles.subtitle}>{t("EmergencyContact")}</h3>
        <FormItemInput
          name={"emergencyName"}
          label={t("Name")}
          rules={{ required: false }}
          isSettingInput={true}
        />

        <div className={`${styles.row} ${styles.adaptive}`}>
          <div className={styles.w40}>
            <FormItemSelect
              name={"emergencyPhoneCountryCode"}
              label={t("PhoneCountryCode")}
              rules={{ required: false }}
              items={phoneCountryCodes}
              isSettingInput={true}
              showSearch={true}
            />
          </div>
          <div className={styles.w60}>
            <FormItemInput
              name={"emergencyPhoneNumber"}
              label={t("PhoneNumber")}
              rules={{ required: false }}
              isSettingInput={true}
            />
          </div>
        </div>

        <div className={styles.btnWrapper}>
          <CustomButton text={t("SaveChanges")} type={"submit"} typeStyle={"fill"} />
        </div>
      </Form>
    </div>
  );
};

export default Contact;
